/**
 * The shared primitives.
 *
 * Five of them do the work the design system's rules depend on, and each exists to
 * stop the same mistake being made per screen:
 *
 * - `Skeleton` matches the geometry of what it replaces, so nothing reflows on arrival.
 * - `EmptyState` states an outcome, a REASON, and the one action that resolves it —
 *   and is distinct from loading, because "no results" and "not yet known" are
 *   different claims and showing the first while the second is true is a lie.
 * - `Pagination` states both counts when a filter is active, so "12 of 340" cannot be
 *   misread as "we only have 12".
 * - `Money` and `StockPill` render the two values in this system that are easiest to
 *   get subtly wrong.
 */

import { type ReactElement, type ReactNode, cloneElement, isValidElement, useEffect, useId, useRef } from 'react';
import { formatMoney } from '@serendib/shared';
import { useToasts } from '../lib/stores.js';

/* ========================================================================== */
/* Money                                                                      */
/* ========================================================================== */

/**
 * Money, formatted once.
 *
 * `value` is the decimal string the API sent — never parsed to a number on the way in.
 * `<data value>` gives assistive technology and any future scraper the machine value
 * beside the presented one.
 */
export function Money({
  value,
  currency = 'LKR',
  decimals = true,
  className,
}: {
  value: string | number;
  currency?: string;
  decimals?: boolean;
  className?: string;
}) {
  return (
    <data className={className} value={String(value)}>
      {formatMoney(value, currency, { decimals })}
    </data>
  );
}

/* ========================================================================== */
/* Stock                                                                      */
/* ========================================================================== */

export type StockState = 'in-stock' | 'low-stock' | 'out-of-stock' | 'backorder';

/**
 * Availability, as a word and a colour together.
 *
 * Never colour alone: about one man in twelve cannot separate the green from the amber,
 * and "can I buy this" is not something to communicate in a channel some readers do not
 * have. The dot is decoration; the word carries the meaning.
 */
export function StockPill({ state, available }: { state: StockState; available?: number }) {
  const [cls, label] = {
    'in-stock': ['pill--good', 'In stock'],
    'low-stock': ['pill--warn', available !== undefined ? `Low stock · ${available} left` : 'Low stock'],
    'out-of-stock': ['pill--bad', 'Out of stock'],
    backorder: ['pill--warn', 'Available to order'],
  }[state];

  return (
    <span className={`pill ${cls}`}>
      <span className="dot" aria-hidden="true" />
      {label}
    </span>
  );
}

/* ========================================================================== */
/* Loading and emptiness                                                      */
/* ========================================================================== */

export function Skeleton({ height = 16, width = '100%', radius }: { height?: number | string; width?: number | string; radius?: number }) {
  return (
    <div
      className="skeleton"
      style={{ height, width, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

/** A block of skeleton lines matching a card's geometry. */
export function SkeletonCards({ count = 4, height = 260 }: { count?: number; height?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} height={height} radius={22} />
      ))}
    </>
  );
}

/**
 * An empty state is a CLAIM, so it waits for the answer.
 *
 * The caller must not render this while a query is in flight — read `isLoading`, never
 * `data ?? []`. Asserting "no products found" over a request that has not returned is
 * the most common way a slow connection is reported as a broken site.
 */
/**
 * `headingLevel` exists because this component is used in two different roles.
 *
 * Usually it sits INSIDE a page that already has its own `h1` — "no products match
 * these filters" — and `h3` is the right level. On the not-found page it is the only
 * thing on the screen, and defaulting to `h3` left that page with no `h1` at all and a
 * heading order that starts at three.
 */
export function EmptyState({
  title,
  reason,
  action,
  headingLevel = 'h3',
}: {
  title: string;
  reason?: string;
  action?: ReactNode;
  headingLevel?: 'h1' | 'h2' | 'h3';
}) {
  const Heading = headingLevel;
  return (
    <div className="empty">
      <Heading>{title}</Heading>
      {reason ? <p className="small measure">{reason}</p> : null}
      {action}
    </div>
  );
}

/* ========================================================================== */
/* Pagination                                                                 */
/* ========================================================================== */

export function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  onPage,
  filtered,
  unfilteredTotal,
}: {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPage: (page: number) => void;
  /** True when a filter is applied, so both counts are stated. */
  filtered?: boolean;
  unfilteredTotal?: number;
}) {
  if (total === 0) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  // A window around the current page with ellipses, rather than 40 numbered buttons.
  const pages: (number | '…')[] = [];
  const push = (n: number) => { if (!pages.includes(n)) pages.push(n); };
  push(1);
  if (page - 2 > 2) pages.push('…');
  for (let n = Math.max(2, page - 1); n <= Math.min(totalPages - 1, page + 1); n++) push(n);
  if (page + 2 < totalPages - 1) pages.push('…');
  if (totalPages > 1) push(totalPages);

  return (
    <nav className="spread" style={{ marginTop: 20 }} aria-label="Pagination">
      <p className="xs dim">
        {/* Both counts when filtered: "12 of 340" alone reads as "we only have 12". */}
        {first}–{last} of {total}
        {filtered && unfilteredTotal !== undefined && unfilteredTotal !== total
          ? ` filtered · ${unfilteredTotal} total`
          : ''}
      </p>

      {totalPages > 1 ? (
        <div className="row" style={{ gap: 6 }}>
          <button className="btn btn--secondary btn--sm" onClick={() => onPage(page - 1)} disabled={page <= 1}>
            Previous
          </button>
          {pages.map((n, i) =>
            n === '…' ? (
              <span key={`gap-${i}`} className="xs dim" style={{ padding: '0 4px' }}>…</span>
            ) : (
              <button
                key={n}
                className={`btn btn--sm ${n === page ? 'btn--primary' : 'btn--secondary'}`}
                onClick={() => onPage(n)}
                aria-current={n === page ? 'page' : undefined}
                style={{ minWidth: 38, padding: '0 10px' }}
              >
                {n}
              </button>
            ),
          )}
          <button className="btn btn--secondary btn--sm" onClick={() => onPage(page + 1)} disabled={page >= totalPages}>
            Next
          </button>
        </div>
      ) : null}
    </nav>
  );
}

/* ========================================================================== */
/* Tabs                                                                       */
/* ========================================================================== */

/**
 * Tabs, not routes, when several views share one sidebar entry.
 *
 * `count` is optional and worth passing: a queue that announces its own length saves
 * the metric tile that would otherwise restate it.
 */
export function TabBar<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string; count?: number }[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="row" role="tablist" style={{ gap: 6, borderBottom: '1px solid var(--hairline)', paddingBottom: 2 }}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={tab.id === active}
          onClick={() => onChange(tab.id)}
          className={`btn btn--sm ${tab.id === active ? 'btn--dark' : 'btn--ghost'}`}
        >
          {tab.label}
          {tab.count !== undefined ? (
            <span
              className="xs"
              style={{
                background: tab.id === active ? 'rgba(255,255,255,0.2)' : 'var(--wash)',
                borderRadius: 999,
                padding: '1px 7px',
                minWidth: 20,
              }}
            >
              {tab.count}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/* ========================================================================== */
/* Toasts                                                                     */
/* ========================================================================== */

export function ToastHost() {
  const { toasts, dismiss } = useToasts();

  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 300,
        display: 'grid',
        gap: 8,
        maxWidth: 'min(420px, calc(100vw - 32px))',
      }}
      /**
       * `polite`, not `assertive`. A screen reader interrupting mid-sentence to
       * announce "saved" is worse than hearing it a moment later.
       */
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="card"
          style={{
            padding: '13px 16px',
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            borderLeft: `3px solid ${t.kind === 'error' ? 'var(--danger)' : t.kind === 'success' ? 'var(--accent-deep)' : 'var(--ink-3)'}`,
            boxShadow: 'var(--shadow-float)',
          }}
        >
          <p className="small" style={{ flex: 1 }}>{t.message}</p>
          <button className="btn btn--ghost btn--sm" onClick={() => dismiss(t.id)} aria-label="Dismiss" style={{ padding: '0 8px', minHeight: 24 }}>
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

/* ========================================================================== */
/* Modal                                                                      */
/* ========================================================================== */

/**
 * A dialog that traps focus and closes on Escape.
 *
 * Both behaviours are the difference between a dialog and a div that looks like one:
 * without the trap, Tab walks into the page behind and a keyboard user is lost in a
 * screen they cannot see.
 */
export function Modal({
  title,
  children,
  onClose,
  width = 560,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  width?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !ref.current) return;

      const focusable = ref.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    // Scrolling the page behind an open dialog is disorienting on a phone, where the
    // dialog does not visually cover everything.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    ref.current?.querySelector<HTMLElement>('input, button, textarea, select')?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(20,23,26,0.4)',
        backdropFilter: 'blur(3px)',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
      }}
      onMouseDown={(event) => {
        // Only a click that STARTED on the backdrop closes. Otherwise a text selection
        // that began inside the dialog and ended outside it dismisses the whole thing.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="card"
        style={{ width: '100%', maxWidth: width, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', padding: 26 }}
      >
        <div className="spread" style={{ marginBottom: 18 }}>
          <h3>{title}</h3>
          <button className="btn btn--ghost btn--sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ========================================================================== */
/* Small helpers                                                              */
/* ========================================================================== */

/**
 * A labelled form control.
 *
 * The label is **associated** with the control, which it was not: this rendered a bare
 * `<label>` as a SIBLING of the input, with no `htmlFor` and no wrapping. It looks
 * identical and is completely broken for anyone not using their eyes — every field on
 * the checkout, the contact form and the whole back office announced as "edit text",
 * with no name. Clicking the label also did nothing, which sighted people notice too.
 *
 * The id is generated here and pushed DOWN onto the child rather than demanded from
 * every call site, because a rule that has to be remembered forty times is a rule that
 * will be missed. `useId` is stable across server and client and unique per instance,
 * so two "Email" fields on one page do not collide.
 *
 * The hint or the error is wired up as `aria-describedby`, so it is read out after the
 * name instead of being decoration only a sighted user gets. `required` is NOT added to
 * the control — the call sites already set it where they want browser validation, and
 * adding it here would silently start blocking submissions. `aria-required` announces
 * the asterisk, which is otherwise `aria-hidden` and therefore invisible to a reader.
 */
export function Field({
  label,
  hint,
  error,
  children,
  required,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  required?: boolean;
}) {
  const uid = useId();
  const controlId = `${uid}-control`;
  const messageId = `${uid}-message`;
  const labelId = `${uid}-label`;
  const describedBy = error || hint ? messageId : undefined;

  // A single element gets the id. Anything else — a fragment, a group of buttons — is
  // labelled as a group instead, because there is no one control to point at.
  const single = isValidElement(children);
  const control = single
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: (children as ReactElement<{ id?: string }>).props.id ?? controlId,
        'aria-describedby':
          (children as ReactElement<Record<string, unknown>>).props['aria-describedby'] ?? describedBy,
        ...(error ? { 'aria-invalid': true } : {}),
        ...(required ? { 'aria-required': true } : {}),
      })
    : children;

  return (
    <div className="field">
      <label id={labelId} htmlFor={single ? controlId : undefined}>
        {label}
        {required ? <span style={{ color: 'var(--danger)' }} aria-hidden="true"> *</span> : null}
      </label>

      {single ? control : <div role="group" aria-labelledby={labelId} aria-describedby={describedBy}>{control}</div>}

      {/* Error replaces the hint rather than stacking — two messages under one input
          is one more thing to read at the moment someone is already stuck. */}
      {error ? (
        <p className="field-error" id={messageId}>{error}</p>
      ) : hint ? (
        <p className="hint" id={messageId}>{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * The honeypot. Hidden from sight AND from assistive technology, never from a bot.
 *
 * Lives here rather than beside either form because both the enquiry form and the
 * configurator's quote form need it, and two copies of a field whose whole value is
 * that its name and shape match what the server drops is two chances to drift apart.
 */
export function Honeypot({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
      <label htmlFor="website-field">Leave this field empty</label>
      <input
        id="website-field"
        name="website"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        border: '2px solid var(--wash-2)',
        borderTopColor: 'var(--accent-deep)',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }}
    />
  );
}
